import { useCallback, useRef, useState } from "react";
import { SWATCHES, colorName, moveInGrid, normalizeHex, textOn } from "../lib/classColors";
import { Dialog } from "../ui/Dialog";

/**
 * A class's colour, picked from a palette (customer feedback, Oliver: "I don't know the hex code of a colour"). One
 * click on a swatch; "More colours…" opens the browser's own picker for anything else; the code can still be typed.
 * A dot marks a colour another class already has. The preview is how the canvas will draw the class.
 * The desktop's QColorDialog already has a palette, so this is the web's alone.
 */
export function ClassColorDialog({ title, name, initial, others, onDone }: {
  title: string;
  /** The class the colour is for, shown in the preview. */
  name: string;
  initial: string;
  /** The other classes, so a colour already in use can be marked. */
  others: { name: string; color_hex: string }[];
  /** The colour picked, or null on Cancel. */
  onDone: (hex: string | null) => void;
}) {
  const [text, setText] = useState(normalizeHex(initial) ?? initial);
  const picked = normalizeHex(text);
  const grid = useRef<HTMLDivElement>(null);
  const at = SWATCHES.findIndex((s) => s.hex === picked);
  const usedBy = (hex: string) => others.filter((o) => o.color_hex.toUpperCase() === hex).map((o) => o.name);

  const onKey = (e: React.KeyboardEvent) => {
    const to = moveInGrid(Math.max(at, 0), e.key);
    if (to === null) return;
    e.preventDefault();
    setText(SWATCHES[to].hex);
    grid.current?.querySelectorAll<HTMLButtonElement>("[role=radio]")[to]?.focus();
  };
  const ok = () => { if (picked) onDone(picked); };
  // Stable, because Dialog moves focus to its first control whenever onClose changes: typing a code re-renders.
  const done = useRef(onDone);
  done.current = onDone;
  const cancel = useCallback(() => done.current(null), []);

  return (
    <Dialog title={title} onClose={cancel} width={460} testId="class-color">
      <div className="swatches" role="radiogroup" aria-label="Colours" ref={grid} onKeyDown={onKey} data-testid="swatches">
        {SWATCHES.map((s, i) => {
          const users = usedBy(s.hex);
          return (
            <button key={s.hex} type="button" role="radio" aria-checked={s.hex === picked} tabIndex={i === Math.max(at, 0) ? 0 : -1}
              className="swatch-btn" style={{ background: s.hex }} aria-label={s.name} data-testid={`swatch-${s.name}`}
              title={`${s.name} ${s.hex}${users.length ? ` · used by ${users.join(", ")}` : ""}`}
              onClick={() => setText(s.hex)} onDoubleClick={() => onDone(s.hex)}>
              {users.length > 0 && <i className="swatch-used" style={{ background: textOn(s.hex) }} />}
            </button>
          );
        })}
      </div>
      <div className="color-row">
        <label className="btn color-more" title="Any colour, from the browser's colour picker">
          <input type="color" value={picked ?? "#000000"} onChange={(e) => setText(e.target.value)} data-testid="color-native" />
          More colours…
        </label>
        <label className="color-hex">
          <span className="muted">Code</span>
          <input className="input" value={text} spellCheck={false} maxLength={7} onChange={(e) => setText(e.target.value)}
            // preventDefault: closing hands focus back to Change Color, and the Enter's keypress would press it again.
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ok(); } }} aria-invalid={!picked} data-testid="color-hex" />
        </label>
      </div>
      {!picked && <div className="error-text" data-testid="color-error">A colour code is # and six hex digits, like #FF6B35.</div>}
      <div className="color-preview" data-testid="color-preview">
        <span className="color-box" style={{ borderColor: picked ?? "transparent" }}>
          <span className="color-label" style={picked ? { background: picked, color: textOn(picked) } : undefined}>{name}</span>
        </span>
        <span className="muted">{picked ? colorName(picked) : ""}</span>
      </div>
      <div className="dialog-actions">
        <button className="btn" onClick={cancel}>Cancel</button>
        <button className="btn primary" disabled={!picked} onClick={ok} data-testid="color-ok">OK</button>
      </div>
    </Dialog>
  );
}
